import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useTranslatableData } from '../hooks/useTranslatable';
import MenuHeader from "./MenuHeader";
import localizedTitle from '../utils/localizedTitle';
import Modal from 'react-modal';
import useApi from "../hooks/useApi";
import { castDate, asTitle } from '../shared/utils/string';
import GiftConfigurationService from "../services/gift_configuration.service";
import UserService from "../services/user.service";
import ZoneService from "../services/zone.service";
import Select from 'react-select';
import ProductService from "../services/product.service";
import MultilingualInput from './MultilingualInput';
import { Menu, MenuList, MenuItem, IconButton, ListItemText, ListItemIcon } from '@mui/material';
import EditIcon from "@mui/icons-material/Edit";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import imageFile from "../assets/images/upload-file.svg";
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    scrollbarWidth: 'none',
    height: '680px',
  },
};

const customSelectStyles = {
  control: (styles) => ({
    ...styles,
    width: '100%',
    height: '60px',
    padding: '0 15px',
    fontSize: '14px',
    lineHeight: '60px',
    color: '#393D44',
    border: '0',
    borderRadius: '20px',
    background: '#ffffff',
  }),
  indicatorSeparator: (base) => ({
    ...base,
    display: 'none',
  }),
};

const getGiftDragId = (giftId) => `gift-${giftId}`;

const moveItem = (items, fromIndex, toIndex) => {
  const nextItems = [...items];
  const [movedItem] = nextItems.splice(fromIndex, 1);
  nextItems.splice(toIndex, 0, movedItem);
  return nextItems;
};

const combineRefs = (...refs) => (node) => {
  refs.forEach((ref) => {
    if (typeof ref === 'function') {
      ref(node);
    }
  });
};

const DraggableGiftRow = ({ gift, disabled, children }) => {
  const dragId = getGiftDragId(gift.id);
  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({ id: dragId, disabled });
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    setActivatorNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: dragId, disabled });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.55 : 1,
    outline: isOver && !isDragging ? '2px solid #FAEF9B' : undefined,
  };

  return (
    <tr ref={combineRefs(setDroppableNodeRef, setDraggableNodeRef)} style={style}>
      {children({
        attributes,
        listeners,
        setActivatorNodeRef,
        disabled,
      })}
    </tr>
  );
};

const DraggableGiftCard = ({ gift, disabled, children }) => {
  const dragId = getGiftDragId(gift.id);
  const { setNodeRef: setDroppableNodeRef, isOver } = useDroppable({ id: dragId, disabled });
  const {
    attributes,
    listeners,
    setNodeRef: setDraggableNodeRef,
    setActivatorNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: dragId, disabled });

  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.55 : 1,
    outline: isOver && !isDragging ? '2px solid #FAEF9B' : undefined,
  };

  return (
    <div ref={combineRefs(setDroppableNodeRef, setDraggableNodeRef)} className="list-items" style={style}>
      {children({
        attributes,
        listeners,
        setActivatorNodeRef,
        disabled,
      })}
    </div>
  );
};

const getDefaultGiftValidityDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() + 2);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const parseDateOnly = (value) => {
  if (!value) return null;

  if (typeof value === 'string') {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    }
  }

  const date = value instanceof Date ? value : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
};

const addUtcYears = (date, years) => {
  const nextDate = new Date(date);
  nextDate.setUTCFullYear(nextDate.getUTCFullYear() + years);
  return nextDate;
};

const addUtcMonths = (date, months) => {
  const nextDate = new Date(date);
  nextDate.setUTCMonth(nextDate.getUTCMonth() + months);
  return nextDate;
};

const getValidityUnitLabel = (unit, value, language = 'fr') => {
  const isEnglish = language?.startsWith('en');

  if (unit === 'year') {
    if (isEnglish) return value === 1 ? 'year' : 'years';
    return value === 1 ? 'an' : 'ans';
  }

  if (unit === 'month') {
    return isEnglish ? (value === 1 ? 'month' : 'months') : 'mois';
  }

  if (isEnglish) return value === 1 ? 'day' : 'days';
  return value === 1 ? 'jour' : 'jours';
};

const formatGiftValidityDuration = (gift, language, unlimitedLabel, expiredLabel) => {
  const validUntil = gift?.valid_until;

  if (!validUntil) {
    return '';
  }

  const endDate = parseDateOnly(validUntil);

  if (!endDate) {
    return '';
  }

  if (endDate.getUTCFullYear() >= 9000) {
    return unlimitedLabel;
  }

  const startDate = parseDateOnly(gift?.updated_at || gift?.created_at) || parseDateOnly(new Date());

  if (!startDate || endDate < startDate) {
    return expiredLabel;
  }

  let years = endDate.getUTCFullYear() - startDate.getUTCFullYear();
  if (addUtcYears(startDate, years) > endDate) {
    years -= 1;
  }

  let cursor = addUtcYears(startDate, Math.max(0, years));
  let months = (endDate.getUTCFullYear() - cursor.getUTCFullYear()) * 12
    + endDate.getUTCMonth()
    - cursor.getUTCMonth();

  if (addUtcMonths(cursor, months) > endDate) {
    months -= 1;
  }

  months = Math.max(0, months);

  if (years > 0 && months >= 10) {
    years += 1;
    months = 0;
  }

  const parts = [];
  if (years > 0) {
    parts.push(`${years} ${getValidityUnitLabel('year', years, language)}`);
  }

  if (months > 0) {
    parts.push(`${months} ${getValidityUnitLabel('month', months, language)}`);
  }

  if (parts.length > 0) {
    return parts.join(' ');
  }

  cursor = addUtcMonths(addUtcYears(startDate, Math.max(0, years)), months);
  const days = Math.max(0, Math.round((endDate - cursor) / MS_PER_DAY));

  return `${days} ${getValidityUnitLabel('day', days, language)}`;
};

const Gift = () => {
  const { t, i18n } = useTranslation('gift');
  const [modalIsOpen, setIsOpen] = useState(false);
  const [gifts, setGifts] = useState([]);
  const [selectedGift, setSelectedGift] = useState(null); // For editing
  const api = useApi();
  const [alertMessage, setAlertMessage] = useState('');
  const [fileName, setFileName] = useState("");
  const [file, setFile] = useState(null);
  const fileInputRef = useRef(null);

  const [giftFormData, setGiftFormData] = useState({
    id: '',
    name: { en: '', fr: '' },
    description: { en: '', fr: '' },
    configuration: [],
    is_active: 1,
    valid_until: getDefaultGiftValidityDate(),
  });

  const [formErrors, setFormErrors] = useState({
    configuration: false,
    configType: false,
    configProduct: false,
    configValue: false,
    configPrice: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(100);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [filters, setFilters] = useState({
    with: 'media',
    sort: 'ordering',
    page: currentPage,
    per_page: perPage
  });

  const [sortDirection, setSortDirection] = useState(true);
  const [filterData, setFilterData] = useState({
    search: "",
  });

  const timeoutRef = useRef(null);
  const [hoveredRow, setHoveredRow] = useState(null);
  const [productList, setProductList] = useState([]);
  const translatedProductList = useTranslatableData(productList, ['name', 'category.title']);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});

  const [showNewConfigFields, setShowNewConfigFields] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );
  const isOrderingMode = !filters.sort || filters.sort === 'ordering';

  const handleNewConfigClick = () => {
    setShowNewConfigFields(true);
  };

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const isEventOffer = (value) => value === true || value === 'true' || value === 1 || value === '1';

  const calculateEventGiftPricing = (originalPrice, discountAmount) => {
    const normalPrice = toNumber(originalPrice);
    const discount = toNumber(discountAmount);

    return {
      normalPrice,
      discount,
      purchasePrice: Math.max(0, normalPrice - discount),
      redemptionValue: normalPrice,
    };
  };

  const formatChf = (value) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return value;
    return Number.isInteger(numericValue) ? String(numericValue) : numericValue.toFixed(2);
  };

  const formatConfigurationSummary = (config) => {
    const titleLabel = config.custom_title ? t('table.values.customTitle') : t('table.values.title');

    if (isEventOffer(config.event_offer)) {
      return `${t('table.values.event')}: ${t('table.values.yes')} | ${t('table.values.price')}: ${formatChf(config.price)}CHF | ${t('table.values.redeemValue')}: ${formatChf(config.redemption_value ?? config.value)}CHF | ${t('table.values.discount')}: ${formatChf(config.discount_amount)}CHF | ${titleLabel}: ${localizedTitle(config, i18n)}`;
    }

    return `${t('table.values.price')}: ${config.price}CHF | ${titleLabel}: ${localizedTitle(config, i18n)}`;
  };

  const validateForm = () => {
    const errors = {};

    if (showNewConfigFields) {
      errors.configType = !giftFormData.type;
      const eventOffer = isEventOffer(giftFormData.event_offer);
      const eventDiscount = toNumber(giftFormData.discount_amount);
      const eventOriginalPrice = toNumber(giftFormData.original_price);
      errors.configProduct = (giftFormData.type === 'treatment' || eventOffer) && !giftFormData.product_id;
      errors.configValue = eventOffer
        ? (!eventDiscount || eventDiscount <= 0 || eventDiscount >= eventOriginalPrice)
        : (
        (giftFormData.type === 'voucher_percentage' && (!giftFormData.value || giftFormData.value < 1 || giftFormData.value > 100)) ||
        ((giftFormData.type === 'voucher_fixe' || giftFormData.type === 'voucher_credit') && !giftFormData.value)
      );
      errors.configPrice = !giftFormData.price;
    }

    setFormErrors(errors);
    // console.log({ errors })

    return !Object.values(errors).some(error => error);
  };

  const handleAddConfiguration = () => {
    const { type, price, product_id, value, original_price, usage, duo, event_offer, discount_amount, custom_title } = giftFormData;
    const eventOffer = isEventOffer(event_offer);
    const eventPricing = calculateEventGiftPricing(original_price, discount_amount);
    const configPrice = eventOffer ? eventPricing.purchasePrice : price;
    const configValue = eventOffer ? eventPricing.redemptionValue : value;
    const configUsage = eventOffer ? 1 : usage;

    let productName = '';
    let productDuration = '';
    let productCategory = '';
    let productHotel = '';

    if (product_id) {
      const selectedProduct = translatedProductList.find((product) => product.id === product_id);
      if (selectedProduct) {
        productName = selectedProduct.name || 'Unknown Product';
        productDuration = selectedProduct.duration ? `${selectedProduct.duration}` : '';
        productCategory = selectedProduct.category.title || '';
        productHotel = selectedProduct.hotel?.name || '';
      
        if (productDuration) {
          const minutes = parseInt(productDuration);
          if (!isNaN(minutes)) {
            const hours = Math.floor(minutes / 60);
            const remainingMinutes = minutes % 60;
            
            if (hours > 0) {
              productDuration = `${hours}h${remainingMinutes > 0 ? remainingMinutes : ''}`;
            } else {
              productDuration = `${remainingMinutes}min`;
            }
          }
        }
      }
    }

    let typeDurH = value;

    if ((type === "duration" || type === "packDur") && typeDurH >= 60) {
      const minutes = parseInt(typeDurH);
      if (!isNaN(minutes)) {
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        typeDurH = `${hours > 0 ? `${hours}h` : ''}${remainingMinutes > 0 ? `${remainingMinutes}` : ''}`;
      }
    }
    if ((type === "duration" || type === "packDur") && typeDurH < 60) {
      typeDurH = `${typeDurH}min`;
    }

    // Build localized title object (fr + en)
    const baseData = { productName, productDuration, productCategory, productHotel, typeDur: typeDurH, original_price, usage: configUsage, value: configValue };

    const buildTitleForLang = (lang) => {
      const isFr = lang && lang.startsWith('fr');
      switch (type) {
        case 'treatment':
          return `${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? (isFr ? `à l’hotel ${productHotel}` : `at hotel ${productHotel}`) : ''}`.trim();
        case 'duration':
          return isFr ? `Service au choix de ${typeDurH}` : `Choice service of ${typeDurH}`;
        case 'package':
          return isFr
            ? `(au lieu de ${original_price * usage} CHF) - Package ${usage} x ${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? `à l’hotel ${productHotel}` : ''}`
            : `(instead of ${original_price * usage} CHF) - Package ${usage} x ${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? `at hotel ${productHotel}` : ''}`;
        case 'packDur':
          return isFr
            ? `(au lieu de ${original_price * usage} CHF) - Package ${usage} x Service au choix de ${typeDurH}`
            : `(instead of ${original_price * usage} CHF) - Package ${usage} x Choice service of ${typeDurH}`;
        case 'voucher_fixe':
          return product_id
            ? (isFr ? `Bon cadeau d'une valeur de ${configValue} CHF pour ${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? `à l’hotel ${productHotel}` : ''}` : `Gift voucher worth ${configValue} CHF for ${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? `at hotel ${productHotel}` : ''}`)
            : (isFr ? `Bon cadeau d'une valeur de ${configValue} CHF` : `Gift voucher worth ${configValue} CHF`);
        case 'voucher_percentage':
          return product_id
            ? (isFr ? `Bon cadeau de ${value}% pour ${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? `à l’hotel ${productHotel}` : ''}` : `Gift voucher ${value}% for ${productCategory} ${productDuration ? `${productDuration}` : ''} ${productHotel ? `at hotel ${productHotel}` : ''}`)
            : (isFr ? `Bon cadeau de ${value}%` : `Gift voucher ${value}%`);
        case 'voucher_credit':
          return isFr ? `Crédit de ${value} CHF à consommer ${productName ? `pour ${productName}` : ''}` : `Credit of ${value} CHF to spend ${productName ? `for ${productName}` : ''}`;
        default:
          return isFr ? 'Bon cadeau' : 'Gift voucher';
      }
    };

    let titleObj = { fr: buildTitleForLang('fr'), en: buildTitleForLang('en') };
    if (duo) {
      titleObj.fr = `${titleObj.fr} en duo`;
      titleObj.en = `${titleObj.en} duo`;
    }

    const newConfig = {
      title: titleObj,
      price: configPrice,
      type: type,
      product_id: product_id,
      original_price: original_price,
      value: configValue,
      usage: configUsage,
    };

    const hasCustomTitle = custom_title && Object.values(custom_title).some((title) => String(title || '').trim());
    if (hasCustomTitle) {
      newConfig.custom_title = {
        fr: custom_title.fr || '',
        en: custom_title.en || '',
      };
    }

    if (eventOffer) {
      newConfig.event_offer = true;
      newConfig.discount_amount = eventPricing.discount;
      newConfig.purchase_price = eventPricing.purchasePrice;
      newConfig.redemption_value = eventPricing.redemptionValue;
      newConfig.normal_price = eventPricing.normalPrice;
    }

    // console.log("newConfig", newConfig)

    if (!validateForm()) {
      return;
    }

    setGiftFormData((prev) => ({
      ...prev,
      configuration: [...prev.configuration, newConfig],
      product_id: null,
      original_price: '',
      price: '',
      value: '',
      custom_title: { fr: '', en: '' },
      discount_amount: '',
      event_offer: false,
      usage: '',
      duo: false,
    }));
    setShowNewConfigFields(false);
  };

  const toggleRow = (id) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  useEffect(() => {
    fetchGifts();
  }, [currentPage, perPage]);

  useEffect(() => {
    const preventScroll = (e) => {
      if (e.target.type === 'number') {
        e.preventDefault();
      }
    };

    window.addEventListener('wheel', preventScroll, { passive: false });
    return () => {
      window.removeEventListener('wheel', preventScroll);
    };
  }, []);
  const fetchGifts = async (updatedFilters = filters) => {
    try {
      const response = await GiftConfigurationService.index(updatedFilters, api);
      setGifts(response.data);
      setTotal(response.total);
      setLastPage(response.last_page);
      if (response.last_page < currentPage) {
        setFilters({ ...updatedFilters, page: response.last_page })
        setCurrentPage(response.last_page);
      }
    } catch (error) {
      console.error("Error fetching gifts", error);
    }
  };

  const persistGiftOrder = async (orderedGifts, previousGifts) => {
    try {
      await GiftConfigurationService.reorder(orderedGifts.map((gift) => gift.id), api);
      await fetchGifts({ ...filters, sort: 'ordering' });
    } catch (error) {
      setGifts(previousGifts);
      await fetchGifts({ ...filters, sort: 'ordering' });
      console.error("Failed to reorder gifts", error);
    }
  };

  const handleGiftDragEnd = async ({ active, over }) => {
    if (!isOrderingMode || !over || active.id === over.id) {
      return;
    }

    const activeGiftId = Number(String(active.id).replace('gift-', ''));
    const overGiftId = Number(String(over.id).replace('gift-', ''));
    const oldIndex = gifts.findIndex((gift) => gift.id === activeGiftId);
    const newIndex = gifts.findIndex((gift) => gift.id === overGiftId);

    if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) {
      return;
    }

    const previousGifts = gifts;
    const orderedGifts = moveItem(gifts, oldIndex, newIndex);

    setGifts(orderedGifts);
    await persistGiftOrder(orderedGifts, previousGifts);
  };

  const fetchGiftsSort = async (updatedFilters = filters) => {
    try {
      const response = await GiftConfigurationService.index(updatedFilters, api);
      setGifts(response.data);
      setTotal(response.total);
      setLastPage(response.last_page);
      if (response.last_page < currentPage) {
        setFilters({ ...updatedFilters, page: response.last_page })
        setCurrentPage(response.last_page);
      }
    } catch (error) {
      console.error("Error fetching gifts", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, usage, type, checked } = e.target;

    if (e.target.name === "file") {
      // console.log({ e });
      const file = e.target.files[0];
      if (file) {
        setFileName(file.name);
        setFile(file);
      }
    }

    setGiftFormData((prev) => {
      const updatedGiftFormData = {
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      };
      const nextEventOffer = isEventOffer(updatedGiftFormData.event_offer);

      if (name === "event_offer") {
        updatedGiftFormData.usage = 1;

        if (checked && updatedGiftFormData.product_id) {
          const pricing = calculateEventGiftPricing(
            updatedGiftFormData.original_price,
            updatedGiftFormData.discount_amount
          );
          updatedGiftFormData.price = pricing.purchasePrice;
          updatedGiftFormData.value = pricing.redemptionValue;
          updatedGiftFormData.purchase_price = pricing.purchasePrice;
          updatedGiftFormData.redemption_value = pricing.redemptionValue;
          updatedGiftFormData.normal_price = pricing.normalPrice;
        } else if (!checked) {
          updatedGiftFormData.discount_amount = "";
          updatedGiftFormData.purchase_price = "";
          updatedGiftFormData.redemption_value = "";
          updatedGiftFormData.normal_price = "";
        }
      }

      if (name === "discount_amount" && nextEventOffer) {
        const pricing = calculateEventGiftPricing(updatedGiftFormData.original_price, value);
        updatedGiftFormData.price = pricing.purchasePrice;
        updatedGiftFormData.value = pricing.redemptionValue;
        updatedGiftFormData.purchase_price = pricing.purchasePrice;
        updatedGiftFormData.redemption_value = pricing.redemptionValue;
        updatedGiftFormData.normal_price = pricing.normalPrice;
        updatedGiftFormData.usage = 1;
      }

      if (name === "duo" && prev.product_id) {
        const originalPrice = parseFloat(prev.price) || 0;
        const newPrice = checked ? originalPrice * 2 : originalPrice;

        updatedGiftFormData.price = newPrice;
      }

      if (name === "value" && (
        prev.type === "voucher_percentage" ||
        prev.type === "voucher_fixe" ||
        prev.type === "voucher_credit"
      ) && !nextEventOffer) {
        const originalPrice = prev.original_price || value;

        let calculatedPrice = originalPrice;

        if (prev.type === "voucher_percentage" && prev.product_id) {
          calculatedPrice -= (originalPrice * value) / 100;
        } else if ((prev.type === "voucher_fixe" || prev.type === "voucher_credit") && prev.product_id) {
          calculatedPrice -= value;
        }

        if (calculatedPrice < 0) {
          calculatedPrice = 0;
        }
        // Apply duo multiplier if needed
        if (prev.duo) {
          calculatedPrice *= 2;
        }

        updatedGiftFormData.price = calculatedPrice;
      }
      if (name === "usage" && prev.type === "package") {
        const originalPrice = prev.original_price || 0;

        // console.log('heroos', value)

        let original = originalPrice * value;
        updatedGiftFormData.price = original;
      }

      if (name === "usage" && prev.type === "packDur") {
        const originalPrice = prev.original_price || 0;

        let original = originalPrice * value;
        updatedGiftFormData.price = original;
      }

      if (!updatedGiftFormData.product_id && updatedGiftFormData.type !== "package" && updatedGiftFormData.type !== "packDur" && updatedGiftFormData.type !== "duration") {
        updatedGiftFormData.usage = 1;
        updatedGiftFormData.original_price = 0;
        updatedGiftFormData.event_offer = false;
        updatedGiftFormData.discount_amount = "";
        updatedGiftFormData.purchase_price = "";
        updatedGiftFormData.redemption_value = "";
        updatedGiftFormData.normal_price = "";
        if (updatedGiftFormData.type === "voucher_percentage") {
          updatedGiftFormData.price = 0;
        }
      }

      if (updatedGiftFormData.type === "duration") {
        updatedGiftFormData.usage = 1;
        updatedGiftFormData.price = updatedGiftFormData.original_price;
      }
      return updatedGiftFormData;
    });

  };

  const handlePriceChange = (e) => {
    const { value } = e.target;
    setGiftFormData((prev) => {
      let newPrice = parseFloat(value) || 0;

      if (prev.duo && prev.product_id) {
        const originalPrice = parseFloat(prev.price) || 0;
        newPrice = originalPrice * 2;
      }

      return {
        ...prev,
        price: newPrice
      };
    });
  };

  const openModal = async (gift = null) => {
    if (gift) {
      setSelectedGift(gift);
      setGiftFormData({
        ...gift,
        // normalize name/description to translation objects if they're strings
        name: typeof gift.name === 'string' ? { en: gift.name } : (gift.name || { en: '', fr: '' }),
        description: typeof gift.description === 'string' ? { en: gift.description } : (gift.description || { en: '', fr: '' }),
        configuration: gift.configuration || [],
        valid_until: gift.valid_until || '',
      });
    } else {
      setSelectedGift(null);
      setGiftFormData({
        name: { en: '', fr: '' },
        description: { en: '', fr: '' },
        configuration: [],
        is_active: 1,
        valid_until: getDefaultGiftValidityDate(),
      });
      setFormErrors({
        configuration: false,
        configType: false,
        configProduct: false,
        configValue: false,
        configPrice: false
      });
      setShowNewConfigFields(false);
      setFile(null);
      setFileName("");
    }

    const products = await ProductService.index({ with: 'category,hotel' }, api);
    setProductList(products);
    setIsOpen(true);
  };


  const closeModal = () => setIsOpen(false);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    const cleanedGiftFormData = {
      name: giftFormData.name,
      description: giftFormData.description,
      is_active: giftFormData.is_active,
      valid_until: giftFormData.valid_until,
      configuration: giftFormData.configuration, // Only send the updated configuration array
    };

    const giftFormDataToSend = new FormData();

    for (const key in giftFormData) {
      const value = giftFormData[key];
      if (value !== undefined && value !== null && value !== '') {
        if (Array.isArray(value)) {
          value.forEach((item, index) => {
            for (const itemKey in item) {
              if (item.hasOwnProperty(itemKey)) {
                let itemValue = item[itemKey];
                if (itemValue === undefined) itemValue = null;

                // If nested object (like title: {en:...,fr:...}) stringify it so backend receives JSON
                if (typeof itemValue === 'object' && itemValue !== null) {
                  giftFormDataToSend.append(`${key}[${index}][${itemKey}]`, JSON.stringify(itemValue));
                } else {
                  giftFormDataToSend.append(`${key}[${index}][${itemKey}]`, itemValue);
                }
              }
            }
          });
        } else if (typeof value === 'object') {
          giftFormDataToSend.append(key, JSON.stringify(value));
        } else if (typeof value === 'boolean') {
          giftFormDataToSend.append(key, Number(value)); // Convert boolean to number
        } else {
          giftFormDataToSend.append(key, value);
        }
      }
    }

    if (file) {
      giftFormDataToSend.append("imageFile", file);
    }

    try {
      const response = await (giftFormData.id ? GiftConfigurationService.updateWithImage(giftFormData.id, file ? giftFormDataToSend : giftFormData, file ? api : api) : GiftConfigurationService.store(file ? giftFormDataToSend : giftFormData, file ? api : api));
      if (response.data) {
        closeModal();
        fetchGifts();
      }
    } catch (e) {
      // errors handled in interceptor
    }
  };

  const handleNextPage = () => {
    setFilters({ ...filters, page: currentPage + 1 });
    setCurrentPage(currentPage + 1);
  }

  const handlePrevPage = () => {
    setFilters({ ...filters, page: currentPage - 1 });
    setCurrentPage(currentPage - 1);
  }

  const handleLastPage = () => {
    setFilters({ ...filters, page: lastPage });
    setCurrentPage(lastPage);
  }

  const handleFirstPage = () => {
    setFilters({ ...filters, page: 1 });
    setCurrentPage(1);
  }

  const handlePerPageChange = (e) => {
    setFilters({ ...filters, per_page: e.target.value, page: 1 });
    setPerPage(e.target.value);
    setCurrentPage(1);
  }

  const handleSelectedSort = async (event) => {
    const selected = document.querySelector('.sort-header.active');
    let direction = true;
    if (selected?.id !== event.target.id) {
      selected?.classList.remove('active');
      event.target.classList.add('active');
      setSortDirection(true);
    } else {
      setSortDirection(!sortDirection);
      direction = !sortDirection;
    }

    const sortField = event.target.id === 'name' ? `${direction ? '' : '-'}name` : `${direction ? '' : '-'}${event.target.id}`;
    setFilters({ ...filters, sort: sortField });
    filters.sort = sortField;

    await fetchGiftsSort();
  };

  

  const handleFilterChange = async (e) => {
    const { name, value } = e.target;
    setFilterData({
      ...filterData,
      [name]: value,
    });

    if (name === "search" && value === "") {
      await fetchGifts();
    }

    if (name === "type") {
      const updatedFilters = { ...filters, type: value };
      await fetchGifts(updatedFilters);
    }
  };

  const handleTypeChange = (e) => {
    const { value } = e.target;

    setGiftFormData((prev) => ({
      ...prev,
      type: value,
      event_offer: value === "voucher_fixe" ? prev.event_offer : false,
      discount_amount: value === "voucher_fixe" ? prev.discount_amount : "",
      purchase_price: value === "voucher_fixe" ? prev.purchase_price : "",
      redemption_value: value === "voucher_fixe" ? prev.redemption_value : "",
      normal_price: value === "voucher_fixe" ? prev.normal_price : "",
    }));
  };


  const productOptions = translatedProductList?.length > 0 ? translatedProductList.map(row => ({ value: row.id, label: `${row.hotel?.name || ' '} - ${row.category?.title || ' '} - ${row.duration}min`, price: row.price })) : [];
  const selectedProductOption = productOptions.find(o => o.value === giftFormData.product_id) || null;

  const handleClick = (event, index) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedIndex(index);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedIndex(null);
  };

  const blockGift = async (gift) => {
    await GiftConfigurationService.update(gift.id, { is_active: false }, api);
    fetchGifts();
  };

  const unblockGift = async (gift) => {
    await GiftConfigurationService.update(gift.id, { is_active: true }, api);
    fetchGifts();
  };

  const toggleGiftStatus = async (gift) => {
    if (isExpired(gift.valid_until)) {
      setAlertMessage(t('alert.expired'));
      setTimeout(() => {
        setAlertMessage('');
      }, 3000);
      return;
    }
    if (gift.is_active) {
      await blockGift(gift);
    } else {
      await unblockGift(gift);
    }
    handleClose();
  };

  const isExpired = (date) => {
  if (!date) return false;
  const currentDate = new Date();
  const valid_until = new Date(date);
  if (isNaN(valid_until.getTime())) return false;
  return valid_until < currentDate;
  };

  const deleteGift = async (gift) => {
    await GiftConfigurationService.delete(gift.id, api);
    fetchGifts();
    handleClose();
  };

  // helper to read string or translation object
  const getLocalized = (val) => {
    if (val === null || val === undefined) return '';
    if (typeof val === 'string') return val;
    if (typeof val === 'object') {
      const lang = i18n?.language || 'en';
      return val[lang] || val.fr || val.en || '';
    }
    return String(val);
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();

    const searchValue = filterData.search.trim().toLowerCase();
    const filteredGift = gifts.filter((gift) =>
      getLocalized(gift.name).toLowerCase().includes(searchValue)
    );

    setGifts(filteredGift);
  };

  const renderOrderHandle = (gift, index, dragHandleProps = {}) => {
    const { attributes, listeners, setActivatorNodeRef, disabled } = dragHandleProps;

    return (
      <button
        type="button"
        ref={setActivatorNodeRef}
        {...(!disabled ? attributes : {})}
        {...(!disabled ? listeners : {})}
        disabled={disabled}
        title={isOrderingMode ? t('actions.dragToReorder') : ''}
        aria-label={isOrderingMode ? t('actions.dragToReorder') : t('table.headers.order')}
        style={{
          border: 0,
          background: 'transparent',
          cursor: disabled ? 'default' : 'grab',
          color: disabled ? '#777' : '#393D44',
          fontWeight: 700,
          minWidth: 42,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          touchAction: 'none',
          userSelect: 'none',
        }}
      >
        <span style={{ fontSize: 16, lineHeight: 1 }}>{isOrderingMode ? '::' : ''}</span>
        <span>{index + 1}</span>
      </button>
    );
  };

  return (
    <>
      <MenuHeader />
      <div className="bloc-search">
        <div className="container">
          <div className="search">
            <div className="search">
              <form onSubmit={handleFilterSubmit} className="text-search">
                <input
                  type="text"
                  name="search"
                  value={filterData.search}
                  onChange={handleFilterChange}
                  placeholder={t('search.placeholder')}
                  className="form-text"
                />
                <input type="submit" className="form-submit" value={t('search.button')} />
              </form>
            </div>
          </div>
          <button onClick={() => openModal()} className="btn-add-client">
            {t('actions.add')}
          </button>
        </div>
      </div>

      <div className="tableau-list view-mobile view-rows">
        <div className="container">
          {gifts?.length > 0 ? (
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGiftDragEnd}>
              {gifts.map((gift, index) => (
                <DraggableGiftCard key={gift.id} gift={gift} disabled={!isOrderingMode}>
                  {(dragHandleProps) => (
                    <>
                      <div className="img-items">
                        {renderOrderHandle(gift, index, dragHandleProps)}
                        <img
                          src={`${gift.media[0]?.original_url}`}
                          alt="Gift image"
                          style={{ width: "50px", height: "50px" }}
                        />
                      </div>
                        <div className="infos-items">
                        <div>Name : {getLocalized(gift.name)}</div>
                        <div>
                          Date of validity : {formatGiftValidityDuration(gift, i18n.language, t('table.values.unlimitedValidation'), t('table.values.expired'))}
                        </div>
                        <div>Description : {getLocalized(gift.description)}</div>
                        <div>{gift.configuration.map((config, index) => (
                          <li key={index}>
                            {formatConfigurationSummary(config)}
                          </li>
                        ))}</div>
                        <div className={gift.is_active ? "active" : "disabled"}>
                          Status : <span>{isExpired(gift.valid_until)
                            ? <span style={{ color: 'red' }}>Expired</span>
                            : gift.is_active
                              ? "Active"
                              : "Disabled"
                          }</span>
                        </div>
                      </div>
                      <div className="menu-edit">
                        <IconButton onClick={(event) => handleClick(event, index)}>
                          <MoreVertIcon />
                        </IconButton>

                        <Menu
                          anchorEl={anchorEl}
                          open={Boolean(anchorEl) && selectedIndex === index}
                          onClose={handleClose}
                          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                          transformOrigin={{ vertical: "top", horizontal: "right" }}
                        >
                          <MenuList style={{ minWidth: 150 }}>
                            <MenuItem onClick={() => { handleClose(); openModal(gift); }}>
                              <ListItemIcon>
                                <EditIcon style={{ color: "#1976d2" }} />
                              </ListItemIcon>
                              <ListItemText>Edit Gift</ListItemText>
                            </MenuItem>
                            {gift.is_active && (
                              <MenuItem style={{ color: 'red' }} onClick={() => toggleGiftStatus(gift)}>
                                <ListItemIcon>
                                  <BlockIcon style={{ color: "#FF5733" }} />
                                </ListItemIcon>
                                <ListItemText>Deactivate Gift</ListItemText>
                              </MenuItem>
                            )}
                            {!gift.is_active && (
                              <MenuItem style={{ color: 'green' }} onClick={() => toggleGiftStatus(gift)}>
                                <ListItemIcon>
                                  <CheckCircleIcon style={{ color: "#28A745" }} />
                                </ListItemIcon>
                                <ListItemText>Activate Gift</ListItemText>
                              </MenuItem>
                            )}
                            {alertMessage && (
                              <MenuItem style={{ color: 'gray' }} disabled>
                                <ListItemText><span dangerouslySetInnerHTML={{ __html: alertMessage }} /></ListItemText>
                              </MenuItem>
                            )}
                            <MenuItem style={{ color: 'red' }} onClick={() => deleteGift(gift)}>
                              <ListItemIcon>
                                <BlockIcon style={{ color: "#FF5733" }} />
                              </ListItemIcon>
                              <ListItemText>Delete Gift</ListItemText>
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </div>
                    </>
                  )}
                </DraggableGiftCard>
              ))}
            </DndContext>
          ) : (
            <div>
              <div colSpan="6">No gifts found.</div>
            </div>
          )}
        </div>
      </div>

      <div className="tableau-list view-desktop">
        <div className="container">
          <table>
            <thead>
              <tr>
                <th className="sort-header active" id="ordering" onClick={handleSelectedSort}>{t('table.headers.order')}</th>
                <th>{t('table.headers.image')}</th>
                <th className="sort-header active" id="name" onClick={handleSelectedSort}>{t('table.headers.name')}</th>
                <th className="sort-header active" id="date_validity" onClick={handleSelectedSort}>{t('table.headers.dateValidity')}</th>
                <th className="sort-header active" id="description" onClick={handleSelectedSort}>{t('table.headers.description')}</th>
                <th className="sort-header active" id="type" onClick={handleSelectedSort}>{t('table.headers.configuration')}</th>
                <th className="sort-header active" id="is_active" onClick={handleSelectedSort}>{t('table.headers.status')}</th>
                <th>{t('table.headers.actions')}</th>
              </tr>
            </thead>
            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleGiftDragEnd}>
              <tbody>
                {gifts?.length > 0 ? (
                  gifts.map((gift, index) => (
                    <DraggableGiftRow key={gift.id} gift={gift} disabled={!isOrderingMode}>
                      {(dragHandleProps) => (
                        <>
                          <td>{renderOrderHandle(gift, index, dragHandleProps)}</td>
                          <td>
                            <img
                              src={`${gift.media[0]?.original_url}`}
                              alt="Gift image"
                              style={{ width: "50px", height: "50px" }}
                            />
                          </td>
                          <td>{getLocalized(gift.name)}</td>
                          <td>
                            {formatGiftValidityDuration(gift, i18n.language, t('table.values.unlimitedValidation'), t('table.values.expired'))}
                          </td>
                          <td>{getLocalized(gift.description)}</td>
                          <td>{gift.configuration.map((config, index) => (
                            <li key={index}>
                              {formatConfigurationSummary(config)}
                            </li>
                          ))}</td>
                          <td className={gift.is_active ? "active" : "disabled"}>
                            {isExpired(gift.valid_until)
                              ? <span style={{ color: 'red' }}>{t('table.values.expired')}</span>
                              : gift.is_active
                                ? t('table.values.active')
                                : t('table.values.disabled')
                            }
                          </td>
                          <td>
                            <IconButton onClick={(event) => handleClick(event, index)}>
                              <MoreVertIcon />
                            </IconButton>

                            <Menu
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl) && selectedIndex === index}
                              onClose={handleClose}
                              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                              transformOrigin={{ vertical: "top", horizontal: "right" }}
                            >
                              <MenuList style={{ minWidth: 150 }}>
                                <MenuItem onClick={() => { handleClose(); openModal(gift); }}>
                                  <ListItemIcon>
                                    <EditIcon style={{ color: "#1976d2" }} />
                                  </ListItemIcon>
                                  <ListItemText>{t('actions.edit')}</ListItemText>
                                </MenuItem>
                                {gift.is_active && (
                                  <MenuItem style={{ color: 'red' }} onClick={() => toggleGiftStatus(gift)}>
                                    <ListItemIcon>
                                      <BlockIcon style={{ color: "#FF5733" }} />
                                    </ListItemIcon>
                                    <ListItemText>{t('actions.deactivate')}</ListItemText>
                                  </MenuItem>
                                )}
                                {!gift.is_active && (
                                  <MenuItem style={{ color: 'green' }} onClick={() => toggleGiftStatus(gift)}>
                                    <ListItemIcon>
                                      <CheckCircleIcon style={{ color: "#28A745" }} />
                                    </ListItemIcon>
                                    <ListItemText>{t('actions.activate')}</ListItemText>
                                  </MenuItem>
                                )}
                                {alertMessage && (
                                  <MenuItem style={{ color: 'gray' }} disabled>
                                    <ListItemText><span dangerouslySetInnerHTML={{ __html: alertMessage }} /></ListItemText>
                                  </MenuItem>
                                )}
                                <MenuItem style={{ color: 'red' }} onClick={() => deleteGift(gift)}>
                                  <ListItemIcon>
                                    <BlockIcon style={{ color: "#FF5733" }} />
                                  </ListItemIcon>
                                  <ListItemText>{t('actions.delete')}</ListItemText>
                                </MenuItem>
                              </MenuList>
                            </Menu>
                          </td>
                        </>
                      )}
                    </DraggableGiftRow>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8">{t('table.values.noGifts')}</td>
                  </tr>
                )}
              </tbody>
            </DndContext>
            <tfoot >
              <tr>
                <td colSpan={8}>
                  <div className="paginator">
                    <select value={perPage} onChange={handlePerPageChange} className="per-page">
                      <option value={1}>1</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                      <option value={250}>250</option>
                      <option value={500}>500</option>
                      <option value={1000}>1000</option>
                    </select>
                    <div className="total">{t('paginator.total')} {total}</div>
                    <div className="pagination">
                      <button type="button" disabled={currentPage === 1} onClick={handleFirstPage}>{'<<'}</button>
                      <button type="button" disabled={currentPage === 1} onClick={handlePrevPage}>{'<'}</button>
                      <span>{currentPage} / {lastPage}</span>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleNextPage}>{'>'}</button>
                      <button type="button" disabled={currentPage === lastPage} onClick={handleLastPage}>{'>>'}</button>
                    </div>
                  </div>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      <Modal
        isOpen={modalIsOpen}
        onRequestClose={closeModal}
        style={customStyles}
        contentLabel={selectedGift ? t('title.edit') : t('title.add')}
      >
        <button className="close-button" onClick={closeModal}>
          <span className="close-icon">&times;</span>
        </button>

        <h2>{selectedGift ? t('title.edit') : t('title.add')}</h2>
        <div className="form-inscription">
          <form onSubmit={handleFormSubmit}>
            <div className="form-text">
              <label>{t('form.name.label')}</label>
              <MultilingualInput
                name="name"
                value={giftFormData.name}
                onChange={(val) => setGiftFormData((prev) => ({ ...prev, name: val }))}
              />
            </div>

            <div className="form-text">
              <label>{t('form.description.label')}</label>
              <MultilingualInput
                name="description"
                value={giftFormData.description}
                onChange={(val) => setGiftFormData((prev) => ({ ...prev, description: val }))}
              />
            </div>
            <div className="champs-notif">
              <input
                type="checkbox"
                name="unlimited_validity"
                checked={giftFormData.valid_until.startsWith('9999')}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  handleInputChange({
                    target: { name: "valid_until", value: isChecked ? '9999-09-09T00:00:00.000000Z' : '' },
                  });
                }}
              />
              <label>
                {t('form.unlimitedValidity.label')}
              </label>
            </div>
            <br />

            {!giftFormData.valid_until.startsWith('9999') && (
              <div className="form-text">
                <label>{t('form.dateValidity.label')}</label>
                <input
                  type="date"
                  name="valid_until"
                  value={giftFormData.valid_until ? giftFormData.valid_until.split('T')[0] : ''}
                  onChange={handleInputChange}
                  required={!giftFormData.valid_until.startsWith('9999')}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

            )}
            <label className="fileLabel" htmlFor="file">
              {t('form.image.label')}
            </label>
            <div className="fileField">
              <label htmlFor="file">
                <img className="imgFile" src={imageFile || ""} alt="File upload" />
                {fileName ? (
                  <div>{t('form.image.uploaded')} {fileName}</div>
                ) : (
                  <>
                    <div className="selectFileTo">{t('form.image.selectFile')}</div>
                    <div className="orDragAnd">
                      {t('form.image.dragDrop')}
                    </div>
                  </>
                )}
              </label>
              <input
                type="file"
                id="file"
                name="file"
                accept="image/*"
                onChange={handleInputChange}
                ref={fileInputRef}
                style={{ display: "none" }}
              />
            </div>

            {giftFormData.configuration.length > 0 && (
              <div className="configuration-display">
                <h2>{t('title.configurations')}</h2>
                <br />
                <ul>
	                  {giftFormData.configuration.map((config, index) => (
	                    <li key={index}>
	                      {formatConfigurationSummary(config)}
	                      <button
                        type="button"
                        onClick={() =>
                          setGiftFormData((prev) => ({
                            ...prev,
                            configuration: prev.configuration.filter((_, i) => i !== index),

                          }))
                        }
                      >
                        {t('form.configuration.remove')}
                      </button>
                    </li>
                  ))}
                </ul>
                <br />
              </div>
            )}
            {/* New Config Button */}
            {!showNewConfigFields ? (
              <button type="button" className="btn-submit" onClick={handleNewConfigClick}>
                {t('form.configuration.new')}
              </button>
            ) : (
              <h2>
                {t('form.configuration.new')}
              </h2>
            )}
            {/* Show New Config Fields if New Config Button Clicked */}
            {showNewConfigFields && (
              <>
                <div className="form-select">
                  <label>{t('form.configuration.type.label')} *</label>
                  <select
                    name="type"
                    value={giftFormData.type}
                    onChange={handleTypeChange}
                    required
                  >
                    <option disabled selected>{t('form.configuration.type.placeholder')}</option>
                    <option value="voucher_percentage">{t('form.configuration.type.options.voucherPercentage')}</option>
                    <option value="voucher_fixe">{t('form.configuration.type.options.voucherFixe')}</option>
                    {/* <option value="voucher_credit">{t('form.configuration.type.options.voucherCredit')}</option> */}
                    <option value="treatment">{t('form.configuration.type.options.treatment')}</option>
                    <option value="duration">{t('form.configuration.type.options.duration')}</option>
                    <option value="package">{t('form.configuration.type.options.package')}</option>
                    <option value="packDur">{t('form.configuration.type.options.packDur')}</option>
	                  </select>
	                  {formErrors.configType && <span className="error-message">Type is required</span>}
	                </div>

	                <div className="form-text">
	                  <label>{t('form.configuration.customTitle.label')}</label>
	                  <MultilingualInput
	                    value={giftFormData.custom_title || { fr: '', en: '' }}
	                    onChange={(customTitle) =>
	                      setGiftFormData((prev) => ({
	                        ...prev,
	                        custom_title: customTitle,
	                      }))
	                    }
	                    defaultLang="fr"
	                    otherLang="en"
	                    placeholder={t('form.configuration.customTitle.placeholder')}
	                  />
	                </div>
	
	                <div className="form-text">
                  {(giftFormData.type !== "duration" && giftFormData.type !== "packDur") && (
                    <>
                      <label>{t('form.configuration.product.label')}</label>
                      <Select
                        isClearable
                        options={productOptions}
                        value={selectedProductOption}
	                        onChange={(selectedOption) =>
	                          setGiftFormData((prev) => {
	                            const originalPrice = selectedOption ? selectedOption.price : "";
	                            let price = originalPrice;
                              let value = prev.type === "treatment" || prev.type === "package"
                                ? selectedOption ? selectedOption.price : ""
                                : prev.value;
                              let eventFields = {};
	
	                            if (prev.duo && selectedOption) {
	                              price = originalPrice * 2;
	                            }

                              if (isEventOffer(prev.event_offer) && selectedOption) {
                                const pricing = calculateEventGiftPricing(originalPrice, prev.discount_amount);
                                price = pricing.purchasePrice;
                                value = pricing.redemptionValue;
                                eventFields = {
                                  purchase_price: pricing.purchasePrice,
                                  redemption_value: pricing.redemptionValue,
                                  normal_price: pricing.normalPrice,
                                  usage: 1,
                                };
                              }
	
	                            return {
	                              ...prev,
	                              product_id: selectedOption ? selectedOption.value : "",
	                              value,
	                              original_price: originalPrice,
	                              price: price,
	                              usage: prev.type !== "package" && prev.type !== "packDur" ? 1 : "",
                                ...eventFields,
	                            };
	                          })
	                        }
                        styles={customSelectStyles}
                        placeholder={t('form.configuration.product.placeholder')}
                      />
                      {formErrors.configProduct && <span className="error-message">Product is required</span>}
                    </>
                  )}

	                  {giftFormData.product_id && selectedProductOption && (
	                    <>
	                      <br />
	                      <label>{t('form.configuration.originalPrice.label')}</label>
	                      <input
                        type="number"
                        name="original_price"
                        value={giftFormData.original_price || selectedProductOption.price}
	                        readOnly
	                      />
	                    </>
	                  )}
                    {giftFormData.type === "voucher_fixe" && giftFormData.product_id && selectedProductOption && (
                      <label className="event-gift-toggle">
                        <input
                          type="checkbox"
                          name="event_offer"
                          checked={isEventOffer(giftFormData.event_offer)}
                          onChange={handleInputChange}
                        />
                        <span className="event-gift-toggle__text">{t('form.configuration.eventOffer.label')}</span>
                        <span className="event-gift-toggle__control" aria-hidden="true" />
                      </label>
                    )}
	                </div>
	
	                <div className="form-text">
                  {giftFormData.type === "voucher_percentage" && (
                    <>
                      <label>{t('form.configuration.value.percentage.label')} *</label>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          type="number"
                          name="value"
                          value={giftFormData.value}
                          onChange={handleInputChange}
                          required
                          min="1"
                          max="100"
                          style={{ flex: 1 }}
                        />
                        <span style={{ marginLeft: "10px" }}>%</span>
                      </div>
                      {formErrors.configValue && <span className="error-message">Percentage is required</span>}
                    </>
                  )}

	                  {(giftFormData.type === "voucher_fixe" || giftFormData.type === "voucher_credit") && !isEventOffer(giftFormData.event_offer) && (
	                    <>
	                      <label>{t('form.configuration.value.amount.label')} *</label>
	                      <input
                        type="number"
                        name="value"
                        value={giftFormData.value}
                        onChange={handleInputChange}
                        required
                        min="0"
                      />
                      {formErrors.configValue && <span className="error-message">Amount is required</span>}
	                    </>
	                  )}

                    {giftFormData.type === "voucher_fixe" && isEventOffer(giftFormData.event_offer) && (
                      <>
                        <label>{t('form.configuration.discountAmount.label')} *</label>
                        <input
                          type="number"
                          name="discount_amount"
                          value={giftFormData.discount_amount}
                          onChange={handleInputChange}
                          required
                          min="0"
                          max={giftFormData.original_price || undefined}
                        />
                        {formErrors.configValue && <span className="error-message">{t('form.configuration.discountAmount.error')}</span>}

                        <br />
                        <label>{t('form.configuration.purchasePrice.label')}</label>
                        <input
                          type="number"
                          name="purchase_price"
                          value={giftFormData.price || 0}
                          readOnly
                        />

                        <br />
                        <label>{t('form.configuration.redeemValue.label')}</label>
                        <input
                          type="number"
                          name="redemption_value"
                          value={giftFormData.value || giftFormData.original_price || 0}
                          readOnly
                        />
                      </>
                    )}

                  {(giftFormData.type === "duration" || giftFormData.type === "packDur") && (
                    <>
                      <label>{t('form.configuration.value.time.label')} *</label>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <input
                          type="number"
                          name="value"
                          value={giftFormData.value}
                          onChange={handleInputChange}
                          required
                          min="0"
                        />
                        <span style={{ marginLeft: "10px" }}>min</span>
                      </div>
                      {formErrors.configValue && <span className="error-message">Duration is required</span>}

                      <br />
                      <label>{t('form.configuration.originalPrice.label')}</label>
                      <input
                        type="number"
                        name="original_price"
                        value={giftFormData.original_price}
                        onChange={handleInputChange}
                      />
                    </>
                  )}

                  {(giftFormData.type === "package" || giftFormData.type === "packDur") && (
                    <>
                      <label>{t('form.configuration.usage.package.label')} *</label>
                      <div>
                        <input
                          type="number"
                          name="usage"
                          value={giftFormData.usage}
                          onChange={handleInputChange}
                          required
                          min="1"
                        />
                      </div>
                    </>
                  )}

                  {giftFormData.type === "voucher_credit" && (
                    <>
                      <label>{t('form.configuration.usage.credit.label')} *</label>
                      <div>
                        <input
                          type="number"
                          name="usage"
                          value={giftFormData.usage}
                          onChange={handleInputChange}
                          required
                          min="1"
                        />
                      </div>
                    </>
                  )}


                  {!giftFormData.product_id && giftFormData.type !== "package" && giftFormData.type !== "voucher_credit" && giftFormData.type !== "packDur" && (
                    <>
                      <label>{t('form.configuration.usage.default.label')}</label>
                      <input
                        type="number"
                        name="usage"
                        value={giftFormData.usage || 1}
                        readOnly
                      />
                    </>
                  )}

	                  {giftFormData.product_id && !isEventOffer(giftFormData.event_offer) && (giftFormData.duo || giftFormData.type === "voucher_fixe" || giftFormData.type === "voucher_percentage" || giftFormData.type === "package" || giftFormData.type === "voucher_credit") && (
	                    <>
	                      <br />
	                      <label>{isEventOffer(giftFormData.event_offer) ? t('form.configuration.purchasePrice.label') : t('form.configuration.price.label')} *</label>
	                      <input
	                        type="number"
	                        name="price"
	                        value={giftFormData.price}
	                        onChange={handlePriceChange}
                          readOnly={isEventOffer(giftFormData.event_offer)}
	                        required
	                        min="0"
	                      />
                      {formErrors.configPrice && <span className="error-message">Price is required</span>}
                    </>
                  )}

                  {!giftFormData.product_id && (
                    <>
                      <br />
                      <label>Price *</label>
                      <input
                        type="number"
                        name="price"
                        value={giftFormData.price}
                        onChange={handlePriceChange}
                        required
                        min="0"
                      />
                      {formErrors.configPrice && <span className="error-message">Price is required</span>}
                    </>
                  )}
                </div>

                {/* <div className="champs-notif">
                  <input type="checkbox" name="duo" checked={giftFormData.duo} onChange={handleInputChange} />
                  <label>{t('form.configuration.duo.label')}</label>
                </div> */}

                {/* Add Configuration as JSON Button */}
                <button
                  type="button"
                  className="btn-submit"
                  onClick={handleAddConfiguration}
                >
                  {t('form.configuration.add')}
                </button>
              </>
            )}

            <button type="submit" className="btn-submit">
              {selectedGift ? t('form.submit.update') : t('form.submit.create')}
            </button>
          </form>
        </div>
      </Modal>

    </>
  );
};

export default Gift;
